// Wave UX-2.1 Item 1 (2026-05-23) — 외부 바이어 웹 i18n 인프라 (Pretendard 정합 lightweight).
// 4언어 ko/en/ar/ru. ARB 패턴 (Flutter Wave UX-1 Item 1.1) JSON 포팅.
//
// 사용:
//   1) main entry 에 <I18nProvider>...</I18nProvider> 감싸기
//   2) 컴포넌트에서:
//        var t = useT();
//        t('navAuctions') → "진행중 경매" / "Active Auctions" / ...
//        t('dashboardWelcome', { name: '홍길동' }) → "환영합니다, 홍길동님"
//
// Locale 우선순위:
//   1. localStorage.autodex_buyer_locale (수동 선택 보존)
//   2. navigator.language → 4언어 매핑
//   3. 'en' fallback
//
// RTL: locale === 'ar' 시 document.documentElement.dir='rtl' 자동 set.

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import koLocale from './locales/ko.json';
import enLocale from './locales/en.json';
import arLocale from './locales/ar.json';
import ruLocale from './locales/ru.json';

var LOCALES = {
  ko: koLocale,
  en: enLocale,
  ar: arLocale,
  ru: ruLocale,
};

var SUPPORTED = ['ko', 'en', 'ar', 'ru'];
var FALLBACK = 'en';
var STORAGE_KEY = 'autodex_buyer_locale';

function resolveInitialLocale() {
  // 1. localStorage
  try {
    var saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.indexOf(saved) >= 0) return saved;
  } catch (e) {}
  // 2. navigator.language (e.g. 'en-US' → 'en')
  try {
    var nav = (navigator.language || '').toLowerCase().split('-')[0];
    if (SUPPORTED.indexOf(nav) >= 0) return nav;
  } catch (e) {}
  // 3. fallback
  return FALLBACK;
}

function interpolate(template, vars) {
  if (!template) return '';
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, function(_, key) {
    return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : '{' + key + '}';
  });
}

var I18nContext = createContext({
  locale: FALLBACK,
  t: function(key) { return key; },
  setLocale: function() {},
  supportedLocales: SUPPORTED,
});

export function I18nProvider(props) {
  var [locale, setLocaleState] = useState(resolveInitialLocale);

  // RTL 자동 설정 + html lang attribute
  useEffect(function() {
    try {
      document.documentElement.lang = locale;
      document.documentElement.dir = (locale === 'ar') ? 'rtl' : 'ltr';
    } catch (e) {}
  }, [locale]);

  var setLocale = function(next) {
    if (SUPPORTED.indexOf(next) < 0) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {}
    setLocaleState(next);
  };

  var t = useMemo(function() {
    var dict = LOCALES[locale] || LOCALES[FALLBACK];
    var fallbackDict = LOCALES[FALLBACK];
    return function(key, vars) {
      var template = dict[key];
      if (template === undefined) {
        // locale 누락 → fallback. fallback 도 없으면 key 그대로 (디버깅 친화).
        template = fallbackDict[key];
        if (template === undefined) return key;
      }
      return interpolate(template, vars);
    };
  }, [locale]);

  var value = useMemo(function() {
    return { locale: locale, t: t, setLocale: setLocale, supportedLocales: SUPPORTED };
  }, [locale, t]);

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useT() {
  return useContext(I18nContext).t;
}

export function useLocale() {
  var ctx = useContext(I18nContext);
  return {
    locale: ctx.locale,
    setLocale: ctx.setLocale,
    supportedLocales: ctx.supportedLocales,
  };
}

// 테스트 / 외부 노출용 (직접 dict 접근)
export { LOCALES, SUPPORTED, FALLBACK };
